import React, { useState, useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { X, Package, Flame, Paintbrush, Wrench, ClipboardList, Info, ChevronDown, ChevronRight, Trash2, Zap, Pencil } from 'lucide-react';
import { FacilityRoom } from '../hooks/useFacilityRooms';
import DatePicker from './DatePicker';
import { api } from '../api/client';
import { useAuth } from '../contexts/AuthContext';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Props {
  room: FacilityRoom;
  facilityId: number;
  onClose: () => void;
  onRoomUpdate?: (updated: Partial<FacilityRoom>) => void;
}

type Tab = 'overview' | 'equipment' | 'extinguishers' | 'paint' | 'fixtures' | 'breakers' | 'workorders';

const TABS: { id: Tab; label: string; icon: React.ReactNode }[] = [
  { id: 'overview',      label: 'Overview',      icon: <Info size={12} /> },
  { id: 'equipment',     label: 'Equipment',      icon: <Package size={12} /> },
  { id: 'extinguishers', label: 'Extinguishers',  icon: <Flame size={12} /> },
  { id: 'paint',         label: 'Paint',          icon: <Paintbrush size={12} /> },
  { id: 'fixtures',      label: 'Fixtures',       icon: <Wrench size={12} /> },
  { id: 'breakers',     label: 'Breakers',       icon: <Zap size={12} /> },
  { id: 'workorders',   label: 'Work Orders',    icon: <ClipboardList size={12} /> },
];

interface Supply {
  id: number;
  name: string;
  supply_type: string | null;
  brand: string | null;
  model: string | null;
  quantity: number;
  location: string | null;
  room_area: string | null;
  condition: string | null;
}

interface Extinguisher {
  id: number;
  type_model: string | null;
  location: string | null;
  quantity: number | null;
  serial_number: string | null;
  last_inspection_date: string | null;
  next_due_date: string | null;
  notes: string | null;
}

interface WallColorEntry {
  id: number;
  room_id: number;
  wall_name: string;
  wall_label: string | null;
  last_painted_date: string | null;
  notes: string | null;
  facility_supply_id: number | null;
  // Joined from facility_supplies
  supply_name: string | null;
  supply_brand: string | null;
  supply_color: string | null;
  supply_finish: string | null;
  supply_image_url: string | null;
}

interface PaintSupply {
  id: number;
  name: string;
  brand: string | null;
  color: string | null;
  finish: string | null;
  image_url: string | null;
  supply_type: string | null;
}

interface BomPart {
  rel_id: number;
  child_item_id: number;
  name: string;
  part_number: string | null;
  quantity: number;
  unit_cost: number | null;
  depth: number;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const SURFACES = ['Room', 'Accent', 'Ceiling', 'Trim', 'Door'] as const;
const FIXTURE_KEYWORDS = ['light', 'fixture', 'lamp', 'electrical', 'bulb'];

const ROOM_TYPE_LABELS: Record<string, string> = {
  resident: 'Resident', medical: 'Medical', common: 'Common Area',
  service: 'Service', mechanical: 'Mechanical', admin: 'Admin',
  hallway: 'Hallway', other: 'Other',
};

const OVERVIEW_ROOM_COLORS: Record<string, { fill: string; stroke: string; text: string }> = {
  resident:   { fill: '#dbeafe', stroke: '#93c5fd', text: '#1e40af' },
  medical:    { fill: '#dcfce7', stroke: '#86efac', text: '#166534' },
  common:     { fill: '#fef9c3', stroke: '#fde047', text: '#713f12' },
  service:    { fill: '#fce7f3', stroke: '#f9a8d4', text: '#9d174d' },
  mechanical: { fill: '#fee2e2', stroke: '#fca5a5', text: '#991b1b' },
  admin:      { fill: '#ede9fe', stroke: '#c4b5fd', text: '#5b21b6' },
  hallway:    { fill: '#f3f4f6', stroke: '#d1d5db', text: '#374151' },
  other:      { fill: '#fff7ed', stroke: '#fdba74', text: '#92400e' },
};

const ROOM_TYPES_ORDERED = Object.keys(OVERVIEW_ROOM_COLORS) as (keyof typeof OVERVIEW_ROOM_COLORS)[];

// ─── Panel ───────────────────────────────────────────────────────────────────

export default function RoomDetailPanel({ room, facilityId, onClose, onRoomUpdate }: Props) {
  const { user } = useAuth();
  const canManage = ['manager', 'org_admin', 'admin'].includes(user?.role ?? '');

  const [activeTab, setActiveTab] = useState<Tab>('overview');

  // Overview: area/location labels
  const [areaLabel, setAreaLabel]         = useState<string | null>(null);
  const [locationLabel, setLocationLabel] = useState<string | null>(null);

  // Overview: edit form state
  const [editType, setEditType]             = useState<string>(room.type);
  const [editName, setEditName]             = useState<string>(room.name);
  const [editRoomNumber, setEditRoomNumber] = useState<string>(room.room_number ?? '');
  const [editWidth, setEditWidth]           = useState<string>(room.dimensions_ft_w?.toString() ?? '');
  const [editHeight, setEditHeight]         = useState<string>(room.dimensions_ft_h?.toString() ?? '');
  const [editNotes, setEditNotes]           = useState<string>(room.notes ?? '');
  const [saveStatus, setSaveStatus]         = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  // Equipment tab
  const [supplies, setSupplies]               = useState<Supply[]>([]);
  const [suppliesLoading, setSuppliesLoading] = useState(false);
  const [suppliesLoaded, setSuppliesLoaded]   = useState(false);

  // Extinguishers tab
  const [extinguishers, setExtinguishers] = useState<Extinguisher[]>([]);
  const [extLoading, setExtLoading]       = useState(false);
  const [extLoaded, setExtLoaded]         = useState(false);

  // Paint tab — entries
  const [paintEntries, setPaintEntries]   = useState<WallColorEntry[]>([]);
  const [paintLoading, setPaintLoading]   = useState(false);
  const [paintLoaded, setPaintLoaded]     = useState(false);
  const [collapsedWalls, setCollapsedWalls] = useState<Set<string>>(new Set());

  // Paint tab — add / edit form
  const [showAddForm, setShowAddForm]           = useState(false);
  const [addWalls, setAddWalls]                 = useState<string[]>([]);
  const [addWallLabel, setAddWallLabel]         = useState('');
  const [addLastPainted, setAddLastPainted]     = useState('');
  const [addNotes, setAddNotes]                 = useState('');
  const [addSelectedSupply, setAddSelectedSupply] = useState<PaintSupply | null>(null);
  const [addSaving, setAddSaving]               = useState(false);
  const [editingEntryId, setEditingEntryId]     = useState<number | null>(null);

  // Paint tab — supply picker
  const [allPaintSupplies, setAllPaintSupplies]   = useState<PaintSupply[]>([]);
  const [paintSuppliesLoading, setPaintSuppliesLoading] = useState(false);
  const [paintSuppliesLoaded, setPaintSuppliesLoaded]   = useState(false);
  const [searchInput, setSearchInput]   = useState('');
  const [searchDisplay, setSearchDisplay] = useState('');
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // Fixtures tab
  const [fixtures, setFixtures]               = useState<Supply[]>([]);
  const [fixturesLoading, setFixturesLoading] = useState(false);
  const [fixturesLoaded, setFixturesLoaded]   = useState(false);
  const [expandedFixture, setExpandedFixture] = useState<number | null>(null);
  const [fixtureParts, setFixtureParts]       = useState<Record<number, BomPart[]>>({});
  const [fixturePartsLoading, setFixturePartsLoading] = useState<Record<number, boolean>>({});

  // Breakers tab
  type RoomBreaker = {
    id: number; panel_id: number; slot_number: number; side: string;
    label: string | null; amperage: number | null; is_double_pole: boolean;
    status: 'on' | 'off' | 'tripped'; notes: string | null;
    panel_name: string; location_note: string | null;
  };
  const [breakers, setBreakers]               = useState<RoomBreaker[]>([]);
  const [breakersLoading, setBreakersLoading] = useState(false);
  const [breakersLoaded, setBreakersLoaded]   = useState(false);

  // ── Load area + location labels on mount ──
  useEffect(() => {
    if (!facilityId || room.linked_area_id == null) return;
    api.get(`/dropdowns?dropdown_type=area&facility_id=${facilityId}`)
      .then(res => {
        const opts = ((res as any).options ?? []) as { id: number; label: string }[];
        const found = opts.find(a => a.id === room.linked_area_id);
        setAreaLabel(found?.label ?? null);
      })
      .catch(() => {});
  }, [facilityId, room.linked_area_id]);

  useEffect(() => {
    if (!facilityId || room.linked_area_id == null || room.linked_location_id == null) {
      setLocationLabel(null);
      return;
    }
    api.get(`/dropdowns?dropdown_type=location&facility_id=${facilityId}&parent_id=${room.linked_area_id}`)
      .then(res => {
        const opts = ((res as any).options ?? []) as { id: number; label: string }[];
        const found = opts.find(l => l.id === room.linked_location_id);
        setLocationLabel(found?.label ?? null);
      })
      .catch(() => {});
  }, [facilityId, room.linked_area_id, room.linked_location_id]);

  // Reset all loaded flags when room changes
  useEffect(() => {
    setSuppliesLoaded(false);
    setExtLoaded(false);
    setPaintLoaded(false);
    setFixturesLoaded(false);
    setPaintEntries([]);
    setShowAddForm(false);
    setCollapsedWalls(new Set());
    setAllPaintSupplies([]);
    setPaintSuppliesLoaded(false);
    setSearchInput('');
    setSearchDisplay('');
    setAddWalls([]);
    setEditingEntryId(null);
    setAddWallLabel('');
    setAddLastPainted('');
    setAddNotes('');
    setAddSelectedSupply(null);
    setFixtures([]);
    setExpandedFixture(null);
    setFixtureParts({});
    setFixturePartsLoading({});
    setBreakersLoaded(false);
    setBreakers([]);
    setActiveTab('overview');
    // Reset overview edit fields
    setEditType(room.type);
    setEditName(room.name);
    setEditRoomNumber(room.room_number ?? '');
    setEditWidth(room.dimensions_ft_w?.toString() ?? '');
    setEditHeight(room.dimensions_ft_h?.toString() ?? '');
    setEditNotes(room.notes ?? '');
    setSaveStatus('idle');
  }, [room.id]);

  // ── Load equipment when tab active ──
  useEffect(() => {
    if (activeTab !== 'equipment' || suppliesLoaded) return;
    setSuppliesLoading(true);
    api.get<Supply[]>('/inventory/supplies')
      .then(res => {
        const all   = res.data ?? [];
        const nameL = room.name.toLowerCase();
        const numL  = (room.room_number ?? '').toLowerCase();
        const locL  = (locationLabel ?? '').toLowerCase();
        const filtered = all.filter(s => {
          const sLoc  = (s.location  ?? '').toLowerCase();
          const sArea = (s.room_area ?? '').toLowerCase();
          return (
            (sArea && (sArea.includes(nameL) || (numL && sArea.includes(numL)) || (locL && sArea.includes(locL)))) ||
            (sLoc  && (sLoc.includes(nameL)  || (numL && sLoc.includes(numL))  || (locL && sLoc.includes(locL))))
          );
        });
        setSupplies(filtered);
        setSuppliesLoaded(true);
      })
      .catch(() => { setSupplies([]); setSuppliesLoaded(true); })
      .finally(() => setSuppliesLoading(false));
  }, [activeTab, suppliesLoaded, room.id, room.name, room.room_number, locationLabel]);

  // ── Load extinguishers when tab active ──
  useEffect(() => {
    if (activeTab !== 'extinguishers' || extLoaded) return;
    setExtLoading(true);
    api.get<Extinguisher[]>(`/facility-rooms/${room.id}/extinguishers`)
      .then(res => { setExtinguishers(res.data ?? []); setExtLoaded(true); })
      .catch(() => { setExtinguishers([]); setExtLoaded(true); })
      .finally(() => setExtLoading(false));
  }, [activeTab, extLoaded, room.id]);

  // ── Load paint entries when tab active ──
  useEffect(() => {
    if (activeTab !== 'paint' || paintLoaded) return;
    setPaintLoading(true);
    api.get<WallColorEntry[]>(`/facility-rooms/${room.id}/wall-colors`)
      .then(res => { setPaintEntries(res.data ?? []); setPaintLoaded(true); })
      .catch(() => { setPaintEntries([]); setPaintLoaded(true); })
      .finally(() => setPaintLoading(false));
  }, [activeTab, paintLoaded, room.id]);

  // ── Load paint supplies when add form opens ──
  useEffect(() => {
    if (!showAddForm || paintSuppliesLoaded) return;
    setPaintSuppliesLoading(true);
    api.get<PaintSupply[]>('/inventory/supplies', { type: 'paint' } as Record<string, unknown>)
      .then(res => { setAllPaintSupplies(res.data ?? []); setPaintSuppliesLoaded(true); })
      .catch(() => { setAllPaintSupplies([]); setPaintSuppliesLoaded(true); })
      .finally(() => setPaintSuppliesLoading(false));
  }, [showAddForm, paintSuppliesLoaded]);

  // ── Load fixtures when tab active ──
  useEffect(() => {
    if (activeTab !== 'fixtures' || fixturesLoaded) return;
    setFixturesLoading(true);
    api.get<Supply[]>('/inventory/supplies')
      .then(res => {
        const all   = res.data ?? [];
        const nameL = room.name.toLowerCase();
        const numL  = (room.room_number ?? '').toLowerCase();
        const locL  = (locationLabel ?? '').toLowerCase();
        const filtered = all.filter(s => {
          const sLoc  = (s.location  ?? '').toLowerCase();
          const sArea = (s.room_area ?? '').toLowerCase();
          const sType = (s.supply_type ?? '').toLowerCase();
          const sName = s.name.toLowerCase();
          const locationMatch = (
            (sArea && (sArea.includes(nameL) || (numL && sArea.includes(numL)) || (locL && sArea.includes(locL)))) ||
            (sLoc  && (sLoc.includes(nameL)  || (numL && sLoc.includes(numL))  || (locL && sLoc.includes(locL))))
          );
          const keywordMatch = FIXTURE_KEYWORDS.some(k => sType.includes(k) || sName.includes(k));
          return locationMatch && keywordMatch;
        });
        setFixtures(filtered);
        setFixturesLoaded(true);
      })
      .catch(() => { setFixtures([]); setFixturesLoaded(true); })
      .finally(() => setFixturesLoading(false));
  }, [activeTab, fixturesLoaded, room.id, room.name, room.room_number, locationLabel]);

  useEffect(() => {
    if (activeTab !== 'breakers' || breakersLoaded) return;
    setBreakersLoading(true);
    api.get(`/breaker-panels/by-room/${room.id}`)
      .then((res: any) => {
        setBreakers(res.data ?? []);
        setBreakersLoaded(true);
      })
      .catch(() => setBreakers([]))
      .finally(() => setBreakersLoading(false));
  }, [activeTab, breakersLoaded, room.id]);

  // ── Computed: filtered paint supply search results ──
  const filteredPaintSupplies = useMemo(() => {
    const term = searchDisplay.trim().toLowerCase();
    const list = term
      ? allPaintSupplies.filter(s =>
          s.name.toLowerCase().includes(term) ||
          (s.brand ?? '').toLowerCase().includes(term) ||
          (s.color ?? '').toLowerCase().includes(term)
        )
      : allPaintSupplies;
    return list.slice(0, 12);
  }, [allPaintSupplies, searchDisplay]);

  // ── Computed: paint entries grouped by wall ──
  const entriesByWall = useMemo(() => {
    const map: Record<string, WallColorEntry[]> = {};
    for (const w of SURFACES) map[w] = [];
    for (const e of paintEntries) {
      if (!map[e.wall_name]) map[e.wall_name] = [];
      map[e.wall_name].push(e);
    }
    return map;
  }, [paintEntries]);

  // ── Paint helpers ──
  function resetAddForm() {
    setAddWalls([]);
    setEditingEntryId(null);
    setAddWallLabel('');
    setAddLastPainted('');
    setAddNotes('');
    setAddSelectedSupply(null);
    setSearchInput('');
    setSearchDisplay('');
  }

  async function deleteEntry(entryId: number) {
    try {
      await api.delete(`/facility-rooms/${room.id}/wall-colors/${entryId}`);
      setPaintEntries(prev => prev.filter(e => e.id !== entryId));
    } catch { /* fail silently */ }
  }

  async function saveEntry() {
    if (!addWalls.length || !addSelectedSupply) return;
    setAddSaving(true);
    try {
      if (editingEntryId != null) {
        const res = await api.put<WallColorEntry>(`/facility-rooms/${room.id}/wall-colors/${editingEntryId}`, {
          wall_name:          addWalls[0],
          facility_supply_id: addSelectedSupply.id,
          wall_label:         addWallLabel || null,
          last_painted_date:  addLastPainted || null,
          notes:              addNotes || null,
        });
        setPaintEntries(prev => prev.map(e => e.id === editingEntryId ? res.data : e));
      } else {
        const newEntries: WallColorEntry[] = [];
        for (const wall of addWalls) {
          const res = await api.post<WallColorEntry>(`/facility-rooms/${room.id}/wall-colors`, {
            wall_name:          wall,
            facility_supply_id: addSelectedSupply.id,
            wall_label:         addWallLabel || null,
            last_painted_date:  addLastPainted || null,
            notes:              addNotes || null,
          });
          newEntries.push(res.data);
        }
        setPaintEntries(prev => [...prev, ...newEntries]);
      }
      setShowAddForm(false);
      resetAddForm();
    } catch { /* fail silently */ }
    finally { setAddSaving(false); }
  }

  // ── Overview save ──
  async function saveOverview() {
    setSaveStatus('saving');
    const payload = {
      type:            editType,
      name:            editName,
      room_number:     editRoomNumber || null,
      dimensions_ft_w: editWidth  !== '' ? Number(editWidth)  : null,
      dimensions_ft_h: editHeight !== '' ? Number(editHeight) : null,
      notes:           editNotes || null,
    };
    try {
      await api.put(`/facility-rooms/${room.id}`, payload);
      setSaveStatus('saved');
      onRoomUpdate?.(payload as Partial<FacilityRoom>);
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  }

  // ── Fixtures helpers ──
  async function toggleFixture(supplyId: number) {
    if (expandedFixture === supplyId) { setExpandedFixture(null); return; }
    setExpandedFixture(supplyId);
    if (fixtureParts[supplyId] !== undefined) return;
    setFixturePartsLoading(prev => ({ ...prev, [supplyId]: true }));
    try {
      const res = await api.get<BomPart[]>(`/inventory/${supplyId}/bom`);
      setFixtureParts(prev => ({ ...prev, [supplyId]: res.data ?? [] }));
    } catch {
      setFixtureParts(prev => ({ ...prev, [supplyId]: [] }));
    } finally {
      setFixturePartsLoading(prev => ({ ...prev, [supplyId]: false }));
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  const panel = (
    <>
      {/* Blur overlay — clicking closes the panel */}
      <div
        style={{
          position: 'fixed', inset: 0, zIndex: 998,
          background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(6px)',
        }}
        onClick={onClose}
      />
      <div
        style={{
          position: 'fixed', top: 0, right: 0, width: 840, height: '100vh',
          background: 'rgba(20,30,50,0.97)', backdropFilter: 'blur(20px)',
          borderLeft: '1px solid rgba(255,255,255,0.1)',
          zIndex: 999, display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}
        onMouseDown={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: '#f1f5f9', fontWeight: 600, fontSize: 15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {room.name}
            </div>
            {room.room_number && (
              <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12, marginTop: 2 }}>
                Room {room.room_number}
              </div>
            )}
            {(areaLabel || locationLabel) && (
              <div style={{ color: '#93c5fd', fontSize: 12, marginTop: 3, display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                {areaLabel && <span>{areaLabel}</span>}
                {areaLabel && locationLabel && <span style={{ color: 'rgba(255,255,255,0.3)' }}>›</span>}
                {locationLabel && <span>{locationLabel}</span>}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', padding: 4, display: 'flex', flexShrink: 0, marginLeft: 8 }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              style={{
                padding: '8px 14px', border: 'none', cursor: 'pointer',
                background: 'transparent',
                borderBottom: activeTab === t.id ? '2px solid #3b82f6' : '2px solid transparent',
                color: activeTab === t.id ? '#60a5fa' : 'rgba(255,255,255,0.4)',
                fontSize: 12, fontWeight: activeTab === t.id ? 600 : 400,
                display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap',
                transition: 'color 0.12s',
              }}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>

          {/* ── Overview ── */}
          {activeTab === 'overview' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* Room Type Picker */}
              <div>
                <div style={ovLabelStyle}>Room Type</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                  {ROOM_TYPES_ORDERED.map(t => {
                    const c = OVERVIEW_ROOM_COLORS[t];
                    const active = editType === t;
                    return (
                      <button
                        key={t}
                        onClick={() => setEditType(t)}
                        style={{
                          fontSize: 11, padding: '4px 10px', borderRadius: 20,
                          cursor: 'pointer', fontWeight: 500,
                          transition: 'all 0.15s',
                          background: active ? c.fill : 'transparent',
                          border: `1px solid ${active ? c.stroke : '#374151'}`,
                          color: active ? c.text : '#6b7280',
                        }}
                      >
                        {ROOM_TYPE_LABELS[t]}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Room Name */}
              <div>
                <div style={ovLabelStyle}>Room Name</div>
                <input
                  style={ovInputStyle}
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                  onFocus={e => (e.currentTarget.style.borderColor = '#3b82f6')}
                  onBlur={e => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.10)')}
                />
              </div>

              {/* Room Number */}
              <div>
                <div style={ovLabelStyle}>Room Number</div>
                <input
                  style={ovInputStyle}
                  value={editRoomNumber}
                  onChange={e => setEditRoomNumber(e.target.value)}
                  onFocus={e => (e.currentTarget.style.borderColor = '#3b82f6')}
                  onBlur={e => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.10)')}
                />
              </div>

              {/* Dimensions */}
              <div>
                <div style={ovLabelStyle}>Dimensions (ft)</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                  <input
                    style={{ ...ovInputStyle, width: '50%' }}
                    placeholder="Width"
                    type="number"
                    min="0"
                    step="0.1"
                    value={editWidth}
                    onChange={e => setEditWidth(e.target.value)}
                    onFocus={e => (e.currentTarget.style.borderColor = '#3b82f6')}
                    onBlur={e => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.10)')}
                  />
                  <input
                    style={{ ...ovInputStyle, width: '50%' }}
                    placeholder="Height"
                    type="number"
                    min="0"
                    step="0.1"
                    value={editHeight}
                    onChange={e => setEditHeight(e.target.value)}
                    onFocus={e => (e.currentTarget.style.borderColor = '#3b82f6')}
                    onBlur={e => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.10)')}
                  />
                </div>
              </div>

              {/* Notes */}
              <div>
                <div style={ovLabelStyle}>Notes</div>
                <textarea
                  style={{ ...ovInputStyle, resize: 'vertical' }}
                  rows={3}
                  value={editNotes}
                  onChange={e => setEditNotes(e.target.value)}
                  onFocus={e => (e.currentTarget.style.borderColor = '#3b82f6')}
                  onBlur={e => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.10)')}
                />
              </div>

              {/* Area / Location (read-only) */}
              {(areaLabel || locationLabel || room.linked_area_id != null) && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {(areaLabel || room.linked_area_id != null) && (
                    <div>
                      <div style={ovLabelStyle}>Area</div>
                      <div style={{ fontSize: 13, color: '#e2e8f0', marginTop: 4 }}>
                        {areaLabel ?? `Area #${room.linked_area_id}`}
                      </div>
                      <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>
                        Managed in Settings › Areas &amp; Locations
                      </div>
                    </div>
                  )}
                  {locationLabel && (
                    <div>
                      <div style={ovLabelStyle}>Location</div>
                      <div style={{ fontSize: 13, color: '#e2e8f0', marginTop: 4 }}>{locationLabel}</div>
                      <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>
                        Managed in Settings › Areas &amp; Locations
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Save button */}
              {canManage && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 4 }}>
                  <button
                    onClick={saveOverview}
                    disabled={saveStatus === 'saving'}
                    style={{
                      background: '#3b82f6', color: 'white', border: 'none',
                      padding: '8px 20px', borderRadius: 8, fontSize: 13,
                      cursor: saveStatus === 'saving' ? 'default' : 'pointer',
                      opacity: saveStatus === 'saving' ? 0.7 : 1,
                    }}
                    onMouseEnter={e => { if (saveStatus !== 'saving') e.currentTarget.style.background = '#2563eb'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#3b82f6'; }}
                  >
                    {saveStatus === 'saving' ? 'Saving…' : 'Save Changes'}
                  </button>
                  {saveStatus === 'saved' && (
                    <span style={{ fontSize: 12, color: '#10b981', fontWeight: 500 }}>Saved ✓</span>
                  )}
                  {saveStatus === 'error' && (
                    <span style={{ fontSize: 12, color: '#ef4444', fontWeight: 500 }}>Save failed</span>
                  )}
                </div>
              )}

            </div>
          )}

          {/* ── Equipment ── */}
          {activeTab === 'equipment' && (
            <div>
              {suppliesLoading ? (
                <Spinner />
              ) : supplies.length === 0 ? (
                <EmptyState
                  icon="📦"
                  title="No equipment linked"
                  hint={`Set the location/room area to "${room.name}" when adding supply items.`}
                />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginBottom: 4 }}>
                    {supplies.length} item{supplies.length !== 1 ? 's' : ''} in this room
                  </div>
                  {supplies.map(s => (
                    <div
                      key={s.id}
                      style={{
                        padding: '10px 12px', borderRadius: 8,
                        background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)',
                      }}
                    >
                      <div style={{ color: '#f1f5f9', fontSize: 13, fontWeight: 500 }}>{s.name}</div>
                      <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, marginTop: 3, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        {s.brand && <span>{s.brand}</span>}
                        {s.model && <span>{s.model}</span>}
                        <span>Qty: {s.quantity}</span>
                        {s.condition && <span style={{ textTransform: 'capitalize' }}>{s.condition}</span>}
                      </div>
                      {s.supply_type && (
                        <div style={{ marginTop: 4, fontSize: 10, color: 'rgba(255,255,255,0.25)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          {s.supply_type}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Extinguishers ── */}
          {activeTab === 'extinguishers' && (
            <div>
              {extLoading ? (
                <Spinner />
              ) : extinguishers.length === 0 ? (
                <EmptyState
                  icon="🧯"
                  title="No extinguishers linked"
                  hint="Link extinguishers to this room by setting room_id on the extinguisher record."
                />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginBottom: 4 }}>
                    {extinguishers.length} extinguisher{extinguishers.length !== 1 ? 's' : ''}
                  </div>
                  {extinguishers.map(ex => {
                    const overdue = ex.next_due_date ? new Date(ex.next_due_date) < new Date() : false;
                    return (
                      <div
                        key={ex.id}
                        style={{
                          padding: '10px 12px', borderRadius: 8,
                          background: 'rgba(255,255,255,0.04)',
                          border: `1px solid ${overdue ? 'rgba(239,68,68,0.3)' : 'rgba(255,255,255,0.07)'}`,
                        }}
                      >
                        <div style={{ color: '#f1f5f9', fontSize: 13, fontWeight: 500 }}>
                          {ex.type_model ?? `Extinguisher #${ex.id}`}
                        </div>
                        <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, marginTop: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {ex.location && <span>Location: {ex.location}</span>}
                          {ex.serial_number && <span>S/N: {ex.serial_number}</span>}
                          {ex.quantity != null && <span>Qty: {ex.quantity}</span>}
                        </div>
                        {(ex.last_inspection_date || ex.next_due_date) && (
                          <div style={{ marginTop: 6, fontSize: 11, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                            {ex.last_inspection_date && (
                              <span style={{ color: 'rgba(255,255,255,0.3)' }}>
                                Last: {ex.last_inspection_date.slice(0, 10)}
                              </span>
                            )}
                            {ex.next_due_date && (
                              <span style={{ color: overdue ? '#fca5a5' : 'rgba(255,255,255,0.3)' }}>
                                {overdue ? '⚠ ' : ''}Next: {ex.next_due_date.slice(0, 10)}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── Paint ── */}
          {activeTab === 'paint' && (
            <div>
              {/* Header row */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                <span style={{ color: '#e2e8f0', fontSize: 13, fontWeight: 600 }}>Wall Paint Records</span>
                {canManage && (
                  <button
                    onClick={() => {
                      setShowAddForm(f => !f);
                      if (showAddForm) resetAddForm();
                    }}
                    style={{ ...btnPrimary, fontSize: 11, padding: '5px 12px' }}
                  >
                    {showAddForm ? '✕ Cancel' : '+ Add Entry'}
                  </button>
                )}
              </div>

              {/* ── Add Entry Form (inline) ── */}
              {showAddForm && (
                <div style={{
                  background: 'rgba(59,130,246,0.06)',
                  border: '1px solid rgba(59,130,246,0.2)',
                  borderRadius: 10, padding: 16, marginBottom: 16,
                }}>

                  {/* Step 1 — Surface selector (multi-select) */}
                  <div style={{ marginBottom: 14 }}>
                    <div style={labelStyle}>
                      {editingEntryId ? 'Step 1 — Change Surface' : 'Step 1 — Select Surface(s)'}
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                      {SURFACES.map(w => {
                        const active = addWalls.includes(w);
                        return (
                        <button
                          key={w}
                          onClick={() => setAddWalls(prev => active ? prev.filter(x => x !== w) : [...prev, w])}
                          style={{
                            padding: '5px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
                            border: active ? '1px solid #3b82f6' : '1px solid rgba(255,255,255,0.15)',
                            background: active ? 'rgba(59,130,246,0.25)' : 'rgba(255,255,255,0.05)',
                            color: active ? '#93c5fd' : 'rgba(255,255,255,0.5)',
                            fontWeight: active ? 600 : 400,
                          }}
                        >
                          {w}
                        </button>
                        );
                      })}
                    </div>
                    <div style={{ marginTop: 10 }}>
                      <input
                        style={{ ...inputStyle, maxWidth: 320 }}
                        placeholder="Label (optional) — e.g. Base coat, Accent, Trim"
                        value={addWallLabel}
                        onChange={e => setAddWallLabel(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Step 2 — Paint picker */}
                  <div style={{ marginBottom: 14 }}>
                    <div style={labelStyle}>Step 2 — Pick Paint from Facility Supplies</div>
                    <input
                      style={{ ...inputStyle, marginTop: 6, marginBottom: 10 }}
                      placeholder="Search paint colors..."
                      value={searchInput}
                      onChange={e => {
                        setSearchInput(e.target.value);
                        clearTimeout(searchTimerRef.current);
                        searchTimerRef.current = setTimeout(() => setSearchDisplay(e.target.value), 300);
                      }}
                    />

                    {paintSuppliesLoading ? (
                      <div style={{ color: '#94a3b8', fontSize: 12, textAlign: 'center', padding: '10px 0' }}>
                        Loading paint supplies…
                      </div>
                    ) : filteredPaintSupplies.length === 0 ? (
                      <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, textAlign: 'center', padding: '10px 0', lineHeight: 1.5 }}>
                        No paint items found.<br />
                        Add paint items in Inventory → Facility Supplies → Paint.
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        {filteredPaintSupplies.map(s => {
                          const isSelected = addSelectedSupply?.id === s.id;
                          return (
                            <div
                              key={s.id}
                              onClick={() => setAddSelectedSupply(isSelected ? null : s)}
                              style={{
                                border: isSelected ? '2px solid #3b82f6' : '1px solid rgba(255,255,255,0.08)',
                                borderRadius: 8,
                                background: isSelected ? 'rgba(59,130,246,0.1)' : 'rgba(255,255,255,0.03)',
                                cursor: 'pointer',
                                overflow: 'hidden',
                                transition: 'border-color 0.12s, background 0.12s',
                              }}
                            >
                              {/* Image or color swatch */}
                              <div style={{ height: 80, overflow: 'hidden', position: 'relative' }}>
                                {s.image_url ? (
                                  <img
                                    src={s.image_url}
                                    alt={s.name}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                  />
                                ) : (
                                  <div style={{
                                    width: '100%', height: '100%',
                                    background: s.color || 'rgba(255,255,255,0.07)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  }}>
                                    <span style={{ fontSize: 22, opacity: 0.6 }}>🎨</span>
                                  </div>
                                )}
                              </div>
                              {/* Info */}
                              <div style={{ padding: '6px 8px' }}>
                                <div style={{ color: '#f1f5f9', fontSize: 12, fontWeight: 500, lineHeight: 1.3 }}>{s.name}</div>
                                <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, marginTop: 2, lineHeight: 1.3 }}>
                                  {[s.brand, s.color, s.finish].filter(Boolean).join(' · ')}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Step 3 — Date + Notes */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
                    <div>
                      <div style={labelStyle}>Last Painted Date <span style={{ opacity: 0.45 }}>(optional)</span></div>
                      <DatePicker
                        value={addLastPainted}
                        onChange={v => setAddLastPainted(v)}
                        placeholder="Select a date"
                        style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, fontSize: 12, padding: '6px 8px' }}
                      />
                    </div>
                    <div>
                      <div style={labelStyle}>Notes <span style={{ opacity: 0.45 }}>(optional)</span></div>
                      <input
                        style={inputStyle}
                        placeholder="e.g. 2 coats, primer used"
                        value={addNotes}
                        onChange={e => setAddNotes(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Save / Cancel */}
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => { setShowAddForm(false); resetAddForm(); }}
                      style={btnGhost}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={saveEntry}
                      disabled={addSaving || !addWalls.length || !addSelectedSupply}
                      style={{
                        ...btnPrimary,
                        opacity: (addSaving || !addWalls.length || !addSelectedSupply) ? 0.45 : 1,
                        cursor: (!addWalls.length || !addSelectedSupply) ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {addSaving ? (editingEntryId ? 'Updating…' : 'Saving…') : (editingEntryId ? 'Update Entry' : 'Save Entry')}
                    </button>
                  </div>
                </div>
              )}

              {/* ── Entries list ── */}
              {paintLoading ? (
                <Spinner />
              ) : paintEntries.length === 0 ? (
                <div style={{ textAlign: 'center', paddingTop: 28 }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>🪣</div>
                  <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13, fontWeight: 500, marginBottom: 6 }}>
                    No paint records yet.
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.25)', fontSize: 12, lineHeight: 1.5, marginBottom: 16 }}>
                    Add your first entry to track colors used in this room.
                  </div>
                  {canManage && !showAddForm && (
                    <button
                      onClick={() => setShowAddForm(true)}
                      style={{ ...btnPrimary, fontSize: 12 }}
                    >
                      + Add Entry
                    </button>
                  )}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {SURFACES.map(wall => {
                    const entries = entriesByWall[wall] ?? [];
                    if (entries.length === 0) return null;

                    const isCollapsed = collapsedWalls.has(wall);
                    return (
                      <div key={wall}>
                        {/* Surface group header */}
                        <div
                          onClick={() => setCollapsedWalls(prev => {
                            const next = new Set(prev);
                            if (next.has(wall)) next.delete(wall); else next.add(wall);
                            return next;
                          })}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            cursor: 'pointer', marginBottom: isCollapsed ? 0 : 8,
                            paddingBottom: 6, borderBottom: '1px solid rgba(255,255,255,0.06)',
                          }}
                        >
                          {isCollapsed
                            ? <ChevronRight size={13} style={{ color: 'rgba(255,255,255,0.3)', flexShrink: 0 }} />
                            : <ChevronDown  size={13} style={{ color: 'rgba(255,255,255,0.3)', flexShrink: 0 }} />
                          }
                          <span style={{ color: '#e2e8f0', fontSize: 13, fontWeight: 600 }}>{wall}</span>
                          <span style={{
                            fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 10,
                            background: 'rgba(59,130,246,0.18)', color: '#93c5fd',
                          }}>
                            {entries.length}
                          </span>
                        </div>

                        {/* Entries */}
                        {!isCollapsed && entries.map(entry => (
                          <div
                            key={entry.id}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 10,
                              padding: '8px 10px', borderRadius: 8, marginBottom: 6,
                              background: 'rgba(255,255,255,0.03)',
                              border: '1px solid rgba(255,255,255,0.06)',
                            }}
                          >
                            {/* Thumbnail */}
                            <div style={{
                              width: 40, height: 40, borderRadius: 6, flexShrink: 0,
                              overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)',
                            }}>
                              {entry.supply_image_url ? (
                                <img
                                  src={entry.supply_image_url}
                                  alt={entry.supply_name ?? ''}
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                              ) : (
                                <div style={{
                                  width: '100%', height: '100%',
                                  background: entry.supply_color || 'rgba(255,255,255,0.08)',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                  <span style={{ fontSize: 16 }}>🎨</span>
                                </div>
                              )}
                            </div>

                            {/* Info */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ color: '#f1f5f9', fontSize: 13, fontWeight: 500, lineHeight: 1.3 }}>
                                {entry.supply_name ?? 'Unknown supply'}
                              </div>
                              <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, marginTop: 2, lineHeight: 1.3 }}>
                                {[entry.supply_brand, entry.supply_color, entry.supply_finish].filter(Boolean).join(' · ')}
                              </div>
                              {entry.wall_label && (
                                <span style={{
                                  display: 'inline-block', marginTop: 4,
                                  fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 10,
                                  background: 'rgba(245,158,11,0.18)', color: '#fcd34d',
                                }}>
                                  {entry.wall_label}
                                </span>
                              )}
                              {entry.last_painted_date && (
                                <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 11, marginTop: 3 }}>
                                  Painted: {entry.last_painted_date.slice(0, 10)}
                                </div>
                              )}
                            </div>

                            {/* Edit / Delete */}
                            {canManage && (
                              <>
                                <button
                                  onClick={() => {
                                    setEditingEntryId(entry.id);
                                    setAddWalls([entry.wall_name]);
                                    setAddWallLabel(entry.wall_label ?? '');
                                    setAddLastPainted((entry.last_painted_date ?? '').slice(0, 10));
                                    setAddNotes(entry.notes ?? '');
                                    if (entry.facility_supply_id) {
                                      setAddSelectedSupply({
                                        id: entry.facility_supply_id,
                                        name: entry.supply_name ?? '',
                                        brand: entry.supply_brand ?? null,
                                        color: entry.supply_color ?? null,
                                        finish: entry.supply_finish ?? null,
                                        image_url: entry.supply_image_url ?? null,
                                        supply_type: null,
                                      });
                                    }
                                    setShowAddForm(true);
                                  }}
                                  title="Edit this paint entry"
                                  style={{
                                    background: 'none', border: 'none', cursor: 'pointer',
                                    color: 'rgba(255,255,255,0.2)', padding: 4, display: 'flex',
                                    borderRadius: 4, flexShrink: 0,
                                    transition: 'color 0.12s',
                                  }}
                                  onMouseEnter={e => (e.currentTarget.style.color = '#93c5fd')}
                                  onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.2)')}
                                >
                                  <Pencil size={14} />
                                </button>
                                <button
                                  onClick={() => deleteEntry(entry.id)}
                                  title="Remove this paint entry"
                                  style={{
                                    background: 'none', border: 'none', cursor: 'pointer',
                                    color: 'rgba(255,255,255,0.2)', padding: 4, display: 'flex',
                                    borderRadius: 4, flexShrink: 0,
                                    transition: 'color 0.12s',
                                  }}
                                  onMouseEnter={e => (e.currentTarget.style.color = '#f87171')}
                                  onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.2)')}
                                >
                                  <Trash2 size={14} />
                                </button>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── Fixtures ── */}
          {activeTab === 'fixtures' && (
            <div>
              {fixturesLoading ? (
                <Spinner />
              ) : fixtures.length === 0 ? (
                <EmptyState
                  icon="💡"
                  title="No fixtures found"
                  hint={`No lighting or fixture items matched to this room. Add supply items with "light", "fixture", "lamp", "electrical", or "bulb" in the name or type.`}
                />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginBottom: 4 }}>
                    {fixtures.length} fixture{fixtures.length !== 1 ? 's' : ''} in this room
                  </div>
                  {fixtures.map(s => {
                    const isExpanded = expandedFixture === s.id;
                    const parts      = fixtureParts[s.id];
                    const partsLoad  = fixturePartsLoading[s.id];

                    return (
                      <div
                        key={s.id}
                        style={{
                          borderRadius: 8, overflow: 'hidden',
                          border: `1px solid ${isExpanded ? 'rgba(59,130,246,0.3)' : 'rgba(255,255,255,0.07)'}`,
                          background: 'rgba(255,255,255,0.04)',
                        }}
                      >
                        <div
                          onClick={() => toggleFixture(s.id)}
                          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', cursor: 'pointer' }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ color: '#f1f5f9', fontSize: 13, fontWeight: 500 }}>{s.name}</div>
                            <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, marginTop: 2, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                              {s.brand && <span>{s.brand}</span>}
                              {s.model && <span>{s.model}</span>}
                              <span>Qty: {s.quantity}</span>
                              {s.condition && <span style={{ textTransform: 'capitalize' }}>{s.condition}</span>}
                            </div>
                            {s.supply_type && (
                              <div style={{ marginTop: 3, fontSize: 10, color: 'rgba(255,255,255,0.2)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                {s.supply_type}
                              </div>
                            )}
                          </div>
                          {isExpanded
                            ? <ChevronDown  size={14} style={{ color: '#60a5fa', flexShrink: 0 }} />
                            : <ChevronRight size={14} style={{ color: 'rgba(255,255,255,0.25)', flexShrink: 0 }} />
                          }
                        </div>

                        {isExpanded && (
                          <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', padding: '8px 12px 12px' }}>
                            {partsLoad ? (
                              <div style={{ color: '#94a3b8', fontSize: 12, padding: '6px 0' }}>Loading parts…</div>
                            ) : parts && parts.length > 0 ? (
                              <div>
                                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                                  Parts / BOM
                                </div>
                                {parts.filter(p => p.depth === 0).map(p => (
                                  <div
                                    key={p.rel_id}
                                    style={{
                                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                      padding: '5px 8px', borderRadius: 6, marginBottom: 4,
                                      background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)',
                                    }}
                                  >
                                    <div>
                                      <div style={{ color: '#e2e8f0', fontSize: 12 }}>{p.name}</div>
                                      <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 11, display: 'flex', gap: 8 }}>
                                        {p.part_number && <span>#{p.part_number}</span>}
                                        {p.unit_cost != null && <span>${Number(p.unit_cost).toFixed(2)}/ea</span>}
                                      </div>
                                    </div>
                                    <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, flexShrink: 0, marginLeft: 8 }}>
                                      × {p.quantity}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div style={{ color: 'rgba(255,255,255,0.2)', fontSize: 12, padding: '4px 0' }}>
                                No parts / BOM linked
                              </div>
                            )}
                            <div style={{ marginTop: 10, display: 'flex', justifyContent: 'flex-end' }}>
                              <button
                                title="Add to purchase order (coming soon)"
                                disabled
                                style={{ ...btnPrimary, opacity: 0.4, cursor: 'not-allowed', fontSize: 11, padding: '5px 12px' }}
                              >
                                + Add to Order
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── Breakers ── */}
          {activeTab === 'breakers' && (
            <div style={{ padding: '12px 0' }}>
              {breakersLoading && (
                <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 12, padding: '20px 0', textAlign: 'center' }}>
                  Loading breakers…
                </div>
              )}
              {!breakersLoading && breakers.length === 0 && (
                <EmptyState
                  icon="⚡"
                  title="No breakers linked"
                  hint="Link breakers to this room from the Facility Map → Breakers tab."
                />
              )}
              {!breakersLoading && breakers.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {breakers.map(b => {
                    const statusColor = b.status === 'on' ? '#10b981' : b.status === 'tripped' ? '#ef4444' : '#64748b';
                    const statusBg = b.status === 'on' ? 'rgba(16,185,129,0.1)' : b.status === 'tripped' ? 'rgba(239,68,68,0.1)' : 'rgba(100,116,139,0.08)';
                    return (
                      <div key={b.id} style={{
                        padding: '10px 12px', borderRadius: 8,
                        border: `1px solid ${statusColor}33`,
                        background: statusBg,
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Zap size={13} style={{ color: statusColor, flexShrink: 0 }} />
                            <span style={{ fontSize: 13, fontWeight: 600, color: '#f1f5f9' }}>
                              {b.label ?? `Slot #${b.slot_number}`}
                            </span>
                          </div>
                          <span style={{
                            fontSize: 10, fontWeight: 600, padding: '1px 7px',
                            borderRadius: 20, textTransform: 'uppercase', letterSpacing: '0.05em',
                            background: statusBg, border: `1px solid ${statusColor}55`,
                            color: statusColor,
                          }}>
                            {b.status}
                          </span>
                        </div>
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                          <span>Panel: {b.panel_name}</span>
                          <span>Slot {b.slot_number} {b.side === 'left' ? 'L' : 'R'}</span>
                          {b.amperage && <span>{b.amperage}A</span>}
                          {b.is_double_pole && <span>2-pole</span>}
                          {b.location_note && <span>· {b.location_note}</span>}
                        </div>
                        {b.notes && (
                          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginTop: 4, fontStyle: 'italic' }}>
                            {b.notes}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── Work Orders ── */}
          {activeTab === 'workorders' && (
            <EmptyState icon="🚧" title="Coming soon" hint="Work order history for this room will appear here." />
          )}

        </div>
      </div>
    </>
  );

  return ReactDOM.createPortal(panel, document.body);
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const labelStyle: React.CSSProperties = {
  fontSize: 11, color: 'rgba(255,255,255,0.45)',
  textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4,
};

const ovLabelStyle: React.CSSProperties = {
  fontSize: 11, fontWeight: 500, textTransform: 'uppercase',
  letterSpacing: '0.07em', color: '#6b7280', marginBottom: 6,
};

const ovInputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', borderRadius: 8,
  border: '1px solid rgba(255,255,255,0.10)',
  background: 'rgba(255,255,255,0.045)',
  color: '#f9fafb', fontSize: 13, outline: 'none',
  boxSizing: 'border-box',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 6,
  color: '#e2e8f0',
  fontSize: 12,
  padding: '6px 8px',
  outline: 'none',
  boxSizing: 'border-box',
};

const btnPrimary: React.CSSProperties = {
  background: '#3b82f6',
  border: 'none',
  borderRadius: 6,
  color: '#fff',
  fontSize: 12,
  fontWeight: 500,
  padding: '6px 16px',
  cursor: 'pointer',
};

const btnGhost: React.CSSProperties = {
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 6,
  color: 'rgba(255,255,255,0.5)',
  fontSize: 12,
  padding: '6px 14px',
  cursor: 'pointer',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={labelStyle}>{label}</div>
      <div style={{ marginTop: 4, color: '#e2e8f0', fontSize: 13 }}>{value}</div>
    </div>
  );
}

function Spinner() {
  return (
    <div style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', paddingTop: 28 }}>
      Loading…
    </div>
  );
}

function EmptyState({ icon, title, hint }: { icon: string; title: string; hint: string }) {
  return (
    <div style={{ textAlign: 'center', paddingTop: 28 }}>
      <div style={{ fontSize: 28, marginBottom: 8 }}>{icon}</div>
      <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13, fontWeight: 500, marginBottom: 6 }}>{title}</div>
      <div style={{ color: 'rgba(255,255,255,0.25)', fontSize: 12, lineHeight: 1.5 }}>{hint}</div>
    </div>
  );
}
